import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton,
  InputAdornment, MenuItem, Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add, BrokenImage, DeleteOutline, EditOutlined, Refresh, WarningAmber } from '@mui/icons-material';
import { GridColDef } from '@mui/x-data-grid';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import DataTableCard from '../../components/ui/DataTableCard';
import EnhancedDataGrid from '../../components/ui/EnhancedDataGrid';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import FormFileUpload from '../../components/ui/forms/FormFileUpload';
import { getMasters, createMaster, updateMaster, deleteMaster } from '../../services/platform/masterService';
import { getApiErrorMessage } from '../../services/userService';
import { isNetworkError } from '../../utils/networkError';
import {
  HOME_BANNER_TYPE,
  HOME_BANNER_MODULES,
  HOME_BANNER_SCREENS,
  HOME_BANNER_SCREENS_BY_MODULE,
  HOME_BANNER_LIMITS,
  HOME_BANNER_BADGE_PATTERN,
  HOME_BANNER_IMAGE_PATTERN,
  HOME_BANNER_URL_PATTERN,
  repetitionError,
  moduleLabel,
  screenLabel,
  defaultScreenForModule,
  suggestBannerCode,
  bannerActionType,
} from '../../constants/homeBanners';

const DISPLAY_DATE_FORMAT = 'MM/DD/YYYY';

interface BannerMeta {
  imageUrl?: string;
  module?: string;
  targetScreen?: string;
  title?: string;
  subtitle?: string;
  url?: string;
  ctaLabel?: string;
  badge?: string;
  startDate?: string;
  endDate?: string;
  sortOrder?: number;
}

interface BannerRow {
  _id: string;
  code: string;
  name: string;
  description?: string;
  sortOrder?: number;
  isActive: boolean;
  metadata?: BannerMeta;
}

type ScheduleState = 'always' | 'scheduled' | 'live' | 'expired';

interface BannerForm {
  name: string;
  description: string;
  module: string;
  targetScreen: string;
  imageUrl: string;
  title: string;
  subtitle: string;
  url: string;
  ctaLabel: string;
  badge: string;
  startDate: string;
  endDate: string;
  sortOrder: string;
}

type FormErrors = Partial<Record<keyof BannerForm, string>>;

const EMPTY_FORM: BannerForm = {
  name: '', description: '', module: '', targetScreen: '', imageUrl: '', title: '', subtitle: '',
  url: '', ctaLabel: '', badge: '', startDate: '', endDate: '', sortOrder: '',
};

function todayIso() {
  return dayjs().format('YYYY-MM-DD');
}

function formatDate(value?: string) {
  if (!value) return '';
  const d = dayjs(value);
  return d.isValid() ? d.format(DISPLAY_DATE_FORMAT) : value;
}

function bannerOrder(row: BannerRow) {
  const top = Number(row.sortOrder);
  if (Number.isFinite(top) && top >= 1) return top;
  const meta = Number(row.metadata?.sortOrder);
  return Number.isFinite(meta) ? meta : 0;
}

function scheduleState(meta?: BannerMeta): ScheduleState {
  const start = meta?.startDate || '';
  const end = meta?.endDate || '';
  const today = todayIso();
  if (end && end < today) return 'expired';
  if (start && start > today) return 'scheduled';
  if (start || end) return 'live';
  return 'always';
}

const SCHEDULE_CHIP: Record<ScheduleState, { label: string; color: 'default' | 'success' | 'info' | 'error' }> = {
  always: { label: 'No end date', color: 'default' },
  live: { label: 'Running', color: 'success' },
  scheduled: { label: 'Scheduled', color: 'info' },
  expired: { label: 'Expired', color: 'error' },
};

function campaignKey(row: BannerRow) {
  const m = row.metadata || {};
  return `${m.module || ''}|${m.targetScreen || ''}|${String(m.imageUrl || '').trim()}`;
}

function nextFreeOrder(rows: BannerRow[], moduleKey: string, excludeId?: string) {
  const used = new Set(
    rows.filter((r) => r._id !== excludeId && r.metadata?.module === moduleKey).map(bannerOrder),
  );
  let n = 1;
  while (used.has(n)) n += 1;
  return n;
}

function rowToForm(row: BannerRow): BannerForm {
  const m = row.metadata || {};
  return {
    name: row.name || '',
    description: row.description || '',
    module: m.module || '',
    targetScreen: m.targetScreen || '',
    imageUrl: m.imageUrl || '',
    title: m.title || '',
    subtitle: m.subtitle || '',
    url: m.url || '',
    ctaLabel: m.ctaLabel || '',
    badge: m.badge || '',
    startDate: m.startDate || '',
    endDate: m.endDate || '',
    sortOrder: String(bannerOrder(row) || ''),
  };
}

function validateForm(form: BannerForm, rows: BannerRow[], editingId: string | undefined, wasActive: boolean): FormErrors {
  const e: FormErrors = {};
  const name = form.name.trim();
  if (!name) e.name = 'Banner name is required';
  else if (name.length > HOME_BANNER_LIMITS.name) e.name = `Max ${HOME_BANNER_LIMITS.name} characters`;
  else {
    const dup = rows.find((r) => r._id !== editingId && r.name.trim().toLowerCase() === name.toLowerCase());
    if (dup) e.name = `Already used by "${dup.code}" — use a distinct campaign name`;
  }

  const description = form.description.trim();
  if (description.length > HOME_BANNER_LIMITS.description) e.description = `Max ${HOME_BANNER_LIMITS.description} characters`;
  else e.description = repetitionError('Description', description);

  if (!form.module) e.module = 'Service Tab is required';
  if (!form.targetScreen) e.targetScreen = 'Open Screen is required';
  else if (form.module && !HOME_BANNER_SCREENS_BY_MODULE[form.module]?.includes(form.targetScreen)) {
    e.targetScreen = `Not a valid screen for ${moduleLabel(form.module)}`;
  }

  if (form.targetScreen === 'external') {
    if (!form.url.trim()) e.url = 'Link URL is required for External URL';
    else if (!HOME_BANNER_URL_PATTERN.test(form.url.trim())) e.url = 'Must start with http:// or https://';
  }

  if (!form.imageUrl.trim()) e.imageUrl = 'Banner Image is required';
  else if (!HOME_BANNER_IMAGE_PATTERN.test(form.imageUrl.trim())) e.imageUrl = 'Upload an image or enter an http(s) URL';
  else if (form.module && form.targetScreen) {
    const key = `${form.module}|${form.targetScreen}|${form.imageUrl.trim()}`;
    const dup = rows.find((r) => r._id !== editingId && campaignKey(r) === key);
    if (dup) e.imageUrl = `"${dup.name}" already uses this image on the same screen — edit it instead of duplicating`;
  }

  const title = form.title.trim();
  if (title.length > HOME_BANNER_LIMITS.title) e.title = `Max ${HOME_BANNER_LIMITS.title} characters`;
  else e.title = repetitionError('Headline', title);
  const subtitle = form.subtitle.trim();
  if (subtitle.length > HOME_BANNER_LIMITS.subtitle) e.subtitle = `Max ${HOME_BANNER_LIMITS.subtitle} characters`;
  else e.subtitle = repetitionError('Subtitle', subtitle);
  if (form.ctaLabel.trim().length > HOME_BANNER_LIMITS.ctaLabel) e.ctaLabel = `Max ${HOME_BANNER_LIMITS.ctaLabel} characters`;

  const badge = form.badge.trim();
  if (badge.length > HOME_BANNER_LIMITS.badge) e.badge = `Max ${HOME_BANNER_LIMITS.badge} characters`;
  else if (badge && !HOME_BANNER_BADGE_PATTERN.test(badge)) e.badge = 'Letters, numbers, spaces and % ₹ $ & + ! . - only';

  if (form.startDate && form.endDate && form.endDate < form.startDate) e.endDate = 'End Date cannot be earlier than Start Date';
  else if (form.endDate && form.endDate < todayIso() && (wasActive || !editingId)) {
    e.endDate = 'End Date is in the past — the banner would never show';
  }

  const order = Number(form.sortOrder);
  if (!form.sortOrder.trim()) e.sortOrder = 'Display Order is required';
  else if (!Number.isInteger(order) || order < 1 || order > 999) e.sortOrder = 'Whole number from 1 to 999';
  else if (form.module) {
    const dup = rows.find((r) => r._id !== editingId && r.metadata?.module === form.module && bannerOrder(r) === order);
    if (dup) e.sortOrder = `Used by "${dup.name}" — next free: ${nextFreeOrder(rows, form.module, editingId)}`;
  }

  for (const k of Object.keys(e) as (keyof BannerForm)[]) if (!e[k]) delete e[k];
  return e;
}

function BannerThumb({ url, onBroken }: { url?: string; onBroken: () => void }) {
  const [failed, setFailed] = useState(false);
  if (!url || failed) {
    return (
      <Tooltip title={url ? 'Image failed to load — replace it' : 'No image'}>
        <Box
          sx={{
            width: 96, height: 40, borderRadius: 1, border: 1, borderColor: 'error.light', bgcolor: 'grey.100',
            display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'error.main',
          }}
        >
          <BrokenImage fontSize="small" />
        </Box>
      </Tooltip>
    );
  }
  return (
    <Box
      component="img"
      src={url}
      alt=""
      loading="lazy"
      onError={() => { setFailed(true); onBroken(); }}
      sx={{ width: 96, height: 40, objectFit: 'cover', borderRadius: 1, border: 1, borderColor: 'divider', display: 'block' }}
    />
  );
}

export default function HomeBannersPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [scheduleFilter, setScheduleFilter] = useState<'all' | ScheduleState>('all');
  const [brokenImages, setBrokenImages] = useState<Set<string>>(new Set());

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<BannerRow | null>(null);
  const [form, setForm] = useState<BannerForm>(EMPTY_FORM);
  const [touched, setTouched] = useState<Partial<Record<keyof BannerForm, boolean>>>({});
  const [submitted, setSubmitted] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<BannerRow | null>(null);
  const [actionError, setActionError] = useState('');

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['masters', HOME_BANNER_TYPE, 'home-banners-page'],
    queryFn: () => getMasters(HOME_BANNER_TYPE, { limit: 500, page: 1 }),
    retry: (count, err) => (isNetworkError(err) ? count < 3 : count < 1),
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
  });
  const rows = useMemo(() => (data?.data ?? []) as BannerRow[], [data]);

  const duplicateNames = useMemo(() => {
    const counts = new Map<string, number>();
    rows.forEach((r) => counts.set(r.name.trim().toLowerCase(), (counts.get(r.name.trim().toLowerCase()) || 0) + 1));
    return new Set([...counts].filter(([, c]) => c > 1).map(([k]) => k));
  }, [rows]);
  const duplicateCampaigns = useMemo(() => {
    const counts = new Map<string, number>();
    rows.forEach((r) => counts.set(campaignKey(r), (counts.get(campaignKey(r)) || 0) + 1));
    return new Set([...counts].filter(([, c]) => c > 1).map(([k]) => k));
  }, [rows]);
  const duplicateRowCount = rows.filter(
    (r) => duplicateNames.has(r.name.trim().toLowerCase()) || duplicateCampaigns.has(campaignKey(r)),
  ).length;

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows
      .filter((r) => {
        const m = r.metadata || {};
        if (moduleFilter && m.module !== moduleFilter) return false;
        if (statusFilter === 'active' && !r.isActive) return false;
        if (statusFilter === 'inactive' && r.isActive) return false;
        if (scheduleFilter !== 'all' && scheduleState(m) !== scheduleFilter) return false;
        if (!q) return true;
        return [r.name, r.code, m.title, m.badge].some((v) => String(v || '').toLowerCase().includes(q));
      })
      .sort((a, b) => String(a.metadata?.module || '').localeCompare(String(b.metadata?.module || ''))
        || bannerOrder(a) - bannerOrder(b));
  }, [rows, search, moduleFilter, statusFilter, scheduleFilter]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['masters', HOME_BANNER_TYPE] });

  const toggleMut = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => updateMaster(id, { isActive }),
    onSuccess: () => { setActionError(''); invalidate(); },
    onError: (err) => setActionError(getApiErrorMessage(err, 'Could not update banner status')),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteMaster(id),
    onSuccess: () => { setDeleteTarget(null); setActionError(''); invalidate(); },
    onError: (err) => { setDeleteTarget(null); setActionError(getApiErrorMessage(err, 'Could not delete banner')); },
  });

  const errors = useMemo(
    () => validateForm(form, rows, editing?._id, editing?.isActive ?? true),
    [form, rows, editing],
  );
  const showError = (key: keyof BannerForm) => (submitted || touched[key] ? errors[key] : undefined);

  const saveMut = useMutation({
    mutationFn: async () => {
      const metadata: Record<string, unknown> = {
        imageUrl: form.imageUrl.trim(),
        module: form.module,
        targetScreen: form.targetScreen,
        title: form.title.trim(),
        subtitle: form.subtitle.trim(),
        url: form.targetScreen === 'external' ? form.url.trim() : '',
        ctaLabel: form.ctaLabel.trim(),
        badge: form.badge.trim(),
        startDate: form.startDate,
        endDate: form.endDate,
        sortOrder: Number(form.sortOrder),
        actionType: bannerActionType(form.targetScreen),
        validFrom: form.startDate || null,
        validTo: form.endDate || null,
      };
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        sortOrder: Number(form.sortOrder),
        metadata,
      };
      if (editing) return updateMaster(editing._id, payload);
      return createMaster(HOME_BANNER_TYPE, { ...payload, isActive: true });
    },
    onSuccess: () => { setDialogOpen(false); invalidate(); },
    onError: (err) => setSaveError(
      isNetworkError(err)
        ? 'Network error — check your connection and try Save again.'
        : getApiErrorMessage(err, 'Could not save banner'),
    ),
  });

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setTouched({});
    setSubmitted(false);
    setSaveError('');
    setDialogOpen(true);
  };
  const openEdit = (row: BannerRow) => {
    setEditing(row);
    setForm(rowToForm(row));
    setTouched({});
    setSubmitted(false);
    setSaveError('');
    setDialogOpen(true);
  };
  const setField = <K extends keyof BannerForm>(key: K, value: BannerForm[K]) => {
    setTouched((t) => ({ ...t, [key]: true }));
    setForm((f) => ({ ...f, [key]: value }));
  };
  const onModuleChange = (moduleKey: string) => {
    setTouched((t) => ({ ...t, module: true }));
    setForm((f) => ({
      ...f,
      module: moduleKey,
      targetScreen: HOME_BANNER_SCREENS_BY_MODULE[moduleKey]?.includes(f.targetScreen)
        ? f.targetScreen
        : defaultScreenForModule(moduleKey),
      sortOrder: !editing || f.module !== moduleKey
        ? String(nextFreeOrder(rows, moduleKey, editing?._id))
        : f.sortOrder,
    }));
  };
  const onSave = () => {
    setSubmitted(true);
    if (Object.keys(errors).length) return;
    setSaveError('');
    saveMut.mutate();
  };

  const codePreview = editing?.code || (form.module && form.name.trim() ? suggestBannerCode(form.module, form.name) : '');
  const screenOptions = form.module
    ? HOME_BANNER_SCREENS.filter((s) => HOME_BANNER_SCREENS_BY_MODULE[form.module]?.includes(s.value))
    : [];

  const columns: GridColDef<BannerRow>[] = [
    {
      field: 'image',
      headerName: 'Image',
      width: 120,
      sortable: false,
      renderCell: (p) => (
        <BannerThumb
          url={p.row.metadata?.imageUrl}
          onBroken={() => setBrokenImages((s) => (s.has(p.row._id) ? s : new Set(s).add(p.row._id)))}
        />
      ),
    },
    {
      field: 'name',
      headerName: 'Banner',
      flex: 1.2,
      minWidth: 220,
      renderCell: (p) => {
        const dupName = duplicateNames.has(p.row.name.trim().toLowerCase());
        const dupCampaign = duplicateCampaigns.has(campaignKey(p.row));
        return (
          <Box sx={{ minWidth: 0, py: 0.5 }}>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="body2" fontWeight={700} noWrap>{p.row.name}</Typography>
              {(dupName || dupCampaign) && (
                <Tooltip title={dupName ? 'Another banner has the same name' : 'Another banner uses the same image on the same screen'}>
                  <Chip size="small" color="warning" icon={<WarningAmber />} label="Duplicate" sx={{ height: 20 }} />
                </Tooltip>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" noWrap display="block">{p.row.code}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'module',
      headerName: 'Service Tab',
      width: 140,
      valueGetter: (_v, row) => moduleLabel(row.metadata?.module),
    },
    {
      field: 'targetScreen',
      headerName: 'Open Screen',
      width: 170,
      renderCell: (p) => {
        const m = p.row.metadata || {};
        const valid = !!m.module && HOME_BANNER_SCREENS_BY_MODULE[m.module]?.includes(m.targetScreen || '');
        return valid ? (
          <Typography variant="body2">{screenLabel(m.targetScreen)}</Typography>
        ) : (
          <Tooltip title="Open Screen does not match the Service Tab — edit to fix">
            <Chip size="small" color="error" variant="outlined" label={screenLabel(m.targetScreen) || 'Not set'} />
          </Tooltip>
        );
      },
    },
    {
      field: 'title',
      headerName: 'Headline',
      flex: 1,
      minWidth: 180,
      renderCell: (p) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" noWrap>{p.row.metadata?.title || '—'}</Typography>
          {p.row.metadata?.badge && <Chip size="small" label={p.row.metadata.badge} sx={{ height: 18, mt: 0.25 }} />}
        </Box>
      ),
    },
    {
      field: 'schedule',
      headerName: 'Schedule',
      width: 200,
      sortable: false,
      renderCell: (p) => {
        const m = p.row.metadata || {};
        const state = scheduleState(m);
        const chip = SCHEDULE_CHIP[state];
        return (
          <Box sx={{ py: 0.5 }}>
            <Chip size="small" color={chip.color} label={chip.label} sx={{ height: 20 }} />
            {(m.startDate || m.endDate) && (
              <Typography variant="caption" color="text.secondary" display="block">
                {m.startDate ? formatDate(m.startDate) : 'Now'} – {m.endDate ? formatDate(m.endDate) : 'No end'}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: 'sortOrder',
      headerName: 'Order',
      width: 80,
      valueGetter: (_v, row) => bannerOrder(row) || '—',
    },
    {
      field: 'isActive',
      headerName: 'Active',
      width: 100,
      sortable: false,
      renderCell: (p) => {
        const expired = scheduleState(p.row.metadata) === 'expired';
        const blocked = expired && !p.row.isActive;
        return (
          <Tooltip title={blocked ? 'End Date has passed — edit the schedule to reactivate' : p.row.isActive ? 'Visible in app' : 'Hidden from app'}>
            <span>
              <Switch
                size="small"
                checked={p.row.isActive}
                disabled={blocked || toggleMut.isPending}
                onChange={(e) => toggleMut.mutate({ id: p.row._id, isActive: e.target.checked })}
              />
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 110,
      sortable: false,
      renderCell: (p) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => openEdit(p.row)}><EditOutlined fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => setDeleteTarget(p.row)}>
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  const activeWithBrokenImage = rows.filter((r) => r.isActive && (!r.metadata?.imageUrl || brokenImages.has(r._id))).length;

  return (
    <PageShell>
      <PageHeader
        title="Home Banners"
        subtitle="Carousel banners on Food, Grocery, and Transport tabs in the mobile app."
        actions={(
          <Button variant="contained" startIcon={<Add />} onClick={openCreate} disabled={isError && !rows.length}>
            Add Banner
          </Button>
        )}
      />

      {isError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button color="inherit" size="small" startIcon={<Refresh />} onClick={() => refetch()} disabled={isFetching}>
              {isFetching ? 'Retrying…' : 'Retry'}
            </Button>
          )}
        >
          {isNetworkError(error)
            ? 'Could not reach the server (network error). Check your connection or VPN, then Retry.'
            : `Could not load banners: ${getApiErrorMessage(error)}`}
        </Alert>
      )}
      {actionError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>{actionError}</Alert>}
      {duplicateRowCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {duplicateRowCount} banner{duplicateRowCount === 1 ? '' : 's'} look duplicated (same name, or same image on the same screen).
          They are marked <strong>Duplicate</strong> below — edit or delete the extra records.
        </Alert>
      )}
      {activeWithBrokenImage > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {activeWithBrokenImage} active banner{activeWithBrokenImage === 1 ? ' has' : 's have'} a missing or broken image.
          Banners without an image are not shown in the app.
        </Alert>
      )}

      <DataTableCard title="Banners" subtitle={`Showing ${filteredRows.length} of ${rows.length}`}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
          <TextField
            size="small"
            label="Search"
            placeholder="Name, code, headline or badge"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 240, flex: 1 }}
          />
          <TextField select size="small" label="Service Tab" value={moduleFilter} onChange={(e) => setModuleFilter(e.target.value)} sx={{ minWidth: 170 }}>
            <MenuItem value="">All</MenuItem>
            {HOME_BANNER_MODULES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)} sx={{ minWidth: 140 }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
          <TextField select size="small" label="Schedule" value={scheduleFilter} onChange={(e) => setScheduleFilter(e.target.value as typeof scheduleFilter)} sx={{ minWidth: 160 }}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="live">Running</MenuItem>
            <MenuItem value="scheduled">Scheduled</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
            <MenuItem value="always">No end date</MenuItem>
          </TextField>
        </Stack>
        <EnhancedDataGrid
          rows={filteredRows}
          columns={columns}
          loading={isLoading}
          getRowId={(r) => r._id}
          rowHeight={60}
          emptyTitle={rows.length ? 'No banners match the filters' : 'No banners yet'}
          emptySubtitle={rows.length ? 'Clear the search or filters to see all banners.' : 'Add a banner to show it on the mobile home carousel.'}
        />
      </DataTableCard>

      <Dialog open={dialogOpen} onClose={() => !saveMut.isPending && setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle fontWeight={700}>{editing ? `Edit Banner — ${editing.name}` : 'Add Banner'}</DialogTitle>
        <DialogContent dividers>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          {submitted && Object.keys(errors).length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>Fix the highlighted fields before saving.</Alert>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <TextField
                fullWidth
                required
                size="small"
                label="Banner name"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                inputProps={{ maxLength: HOME_BANNER_LIMITS.name }}
                error={!!showError('name')}
                helperText={showError('name') || `Unique campaign name · ${form.name.length}/${HOME_BANNER_LIMITS.name}`}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size="small"
                label="Code"
                value={codePreview}
                placeholder="Generated from Service Tab + name"
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Chip size="small" label={editing ? 'Locked' : 'Auto'} />
                    </InputAdornment>
                  ),
                }}
                helperText={editing ? 'Code cannot change after creation' : 'Auto-generated; a number is added if it already exists'}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                size="small"
                label="Description (internal)"
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
                inputProps={{ maxLength: HOME_BANNER_LIMITS.description }}
                error={!!showError('description')}
                helperText={showError('description') || `Campaign notes · ${form.description.length}/${HOME_BANNER_LIMITS.description}`}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                required
                size="small"
                label="Service Tab"
                value={form.module}
                onChange={(e) => onModuleChange(e.target.value)}
                error={!!showError('module')}
                helperText={showError('module') || 'App tab where the banner appears'}
              >
                {HOME_BANNER_MODULES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                required
                size="small"
                label="Open Screen"
                value={form.targetScreen}
                onChange={(e) => setField('targetScreen', e.target.value)}
                disabled={!form.module}
                error={!!showError('targetScreen')}
                helperText={showError('targetScreen') || (form.module ? 'Where a tap on the banner goes' : 'Select a Service Tab first')}
              >
                {screenOptions.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required={form.targetScreen === 'external'}
                size="small"
                label="Link URL"
                value={form.url}
                onChange={(e) => setField('url', e.target.value)}
                disabled={form.targetScreen !== 'external'}
                placeholder={form.targetScreen === 'external' ? 'https://…' : ''}
                error={!!showError('url')}
                helperText={showError('url') || (form.targetScreen === 'external' ? 'Opens in the browser' : 'Only for External URL')}
              />
            </Grid>

            <Grid item xs={12}>
              <FormFileUpload
                label="Banner Image"
                required
                value={form.imageUrl}
                folder="app/home-banners"
                showUrlField
                helperText="Recommended 1200×480 — shown in the customer app home carousel"
                error={!!showError('imageUrl')}
                errorText={showError('imageUrl')}
                onChange={(url) => setField('imageUrl', url)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Headline"
                value={form.title}
                onChange={(e) => setField('title', e.target.value)}
                inputProps={{ maxLength: HOME_BANNER_LIMITS.title }}
                error={!!showError('title')}
                helperText={showError('title') || `${form.title.length}/${HOME_BANNER_LIMITS.title}`}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Subtitle"
                value={form.subtitle}
                onChange={(e) => setField('subtitle', e.target.value)}
                inputProps={{ maxLength: HOME_BANNER_LIMITS.subtitle }}
                error={!!showError('subtitle')}
                helperText={showError('subtitle') || `${form.subtitle.length}/${HOME_BANNER_LIMITS.subtitle}`}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Button Label"
                value={form.ctaLabel}
                onChange={(e) => setField('ctaLabel', e.target.value)}
                inputProps={{ maxLength: HOME_BANNER_LIMITS.ctaLabel }}
                error={!!showError('ctaLabel')}
                helperText={showError('ctaLabel') || `e.g. Order Now · ${form.ctaLabel.length}/${HOME_BANNER_LIMITS.ctaLabel}`}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Badge Text"
                value={form.badge}
                onChange={(e) => setField('badge', e.target.value.toUpperCase())}
                inputProps={{ maxLength: HOME_BANNER_LIMITS.badge }}
                error={!!showError('badge')}
                helperText={showError('badge') || `e.g. 50% OFF · ${form.badge.length}/${HOME_BANNER_LIMITS.badge}`}
              />
            </Grid>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Start Date"
                  format={DISPLAY_DATE_FORMAT}
                  value={form.startDate ? dayjs(form.startDate) : null}
                  onChange={(v) => setField('startDate', v && v.isValid() ? v.format('YYYY-MM-DD') : '')}
                  slotProps={{
                    textField: { fullWidth: true, size: 'small', helperText: 'Optional — shows from this date' },
                    field: { clearable: true },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="End Date"
                  format={DISPLAY_DATE_FORMAT}
                  value={form.endDate ? dayjs(form.endDate) : null}
                  minDate={form.startDate ? dayjs(form.startDate) : undefined}
                  onChange={(v) => setField('endDate', v && v.isValid() ? v.format('YYYY-MM-DD') : '')}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small',
                      error: !!showError('endDate'),
                      helperText: showError('endDate') || 'Optional — hidden after this date',
                    },
                    field: { clearable: true },
                  }}
                />
              </Grid>
            </LocalizationProvider>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                size="small"
                type="number"
                label="Display Order"
                value={form.sortOrder}
                onChange={(e) => setField('sortOrder', e.target.value)}
                disabled={!form.module}
                inputProps={{ min: 1, max: 999, step: 1 }}
                error={!!showError('sortOrder')}
                helperText={showError('sortOrder') || (form.module ? `Unique within ${moduleLabel(form.module)}` : 'Select a Service Tab first')}
              />
            </Grid>
          </Grid>
          {!editing && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
              New banners are created Active. Use the Active switch in the list to hide or show a banner.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saveMut.isPending}>Cancel</Button>
          <Button variant="contained" onClick={onSave} disabled={saveMut.isPending}>
            {saveMut.isPending ? 'Saving…' : editing ? 'Save changes' : 'Create banner'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete banner?"
        message={deleteTarget
          ? `${deleteTarget.isActive ? `"${deleteTarget.name}" is ACTIVE and currently visible in the ${moduleLabel(deleteTarget.metadata?.module)} tab of the app.\nConsider turning it off instead.\n\n` : ''}Delete "${deleteTarget.name}" (${deleteTarget.code})? This cannot be undone.`
          : ''}
        confirmLabel="Delete"
        verifyText={deleteTarget?.isActive ? 'DELETE' : undefined}
        verifyLabel='Type "DELETE" to remove an active banner'
        loading={deleteMut.isPending}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget._id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </PageShell>
  );
}
